import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import BooksitoutButton from './BooksitoutButton'

interface Props {
	message: string
	onClick?: () => void
	disabled?: boolean
	fixed?: boolean
}

const BottomCTA: React.FC<Props> = ({ message, onClick, disabled, fixed = true }) => {
	const ctaRef = useRef<HTMLDivElement>(null)
	const [spacerHeight, setSpacerHeight] = useState(0)
	const [inModal, setInModal] = useState(false)
	const [keyboardOpen, setKeyboardOpen] = useState(false)

	useEffect(() => {
		if (ctaRef.current) {
			setInModal(ctaRef.current.closest('.modal') != null)
		}
	}, [])

	const [keyboardBottomOffset, setKeyboardBottomOffset] = useState(0)

	useEffect(() => {
		const vv = window.visualViewport
		if (!vv) return

		const initialHeight = window.innerHeight

		const handleViewport = () => {
			const isOpen = vv.height < initialHeight * 0.85
			setKeyboardOpen(isOpen)

			if (isOpen) {
				setKeyboardBottomOffset(initialHeight - vv.height)
			} else {
				setKeyboardBottomOffset(0)
			}
		}

		vv.addEventListener('resize', handleViewport)
		vv.addEventListener('scroll', handleViewport)
		return () => {
			vv.removeEventListener('resize', handleViewport)
			vv.removeEventListener('scroll', handleViewport)
		}
	}, [])

	useEffect(() => {
		if ((fixed || inModal) && ctaRef.current) {
			setSpacerHeight(ctaRef.current.clientHeight)
		}
	}, [fixed, inModal])

	const [visible, setVisible] = useState(true)
	const lastScrollY = useRef(0)
	const handleScroll = useCallback(() => {
		const currentScrollY = window.scrollY
		setVisible(currentScrollY <= lastScrollY.current || currentScrollY < 50)
		lastScrollY.current = currentScrollY
	}, [])

	useEffect(() => {
		if (!fixed || inModal) return
		window.addEventListener('scroll', handleScroll, { passive: true })
		return () => window.removeEventListener('scroll', handleScroll)
	}, [fixed, inModal, handleScroll])

	return (
		<>
			{(fixed || inModal) && <MobileSpacer style={{ height: spacerHeight + (keyboardOpen ? keyboardBottomOffset : 0) }} />}
			<Container ref={ctaRef} $fixed={fixed} $inModal={inModal} $visible={visible} $keyboardOpen={keyboardOpen} style={keyboardOpen ? { bottom: `${keyboardBottomOffset}px` } : undefined}>
				{!keyboardOpen && <Gradient $inModal={inModal} />}
				<ButtonArea $inModal={inModal} $keyboardOpen={keyboardOpen}>
					<BooksitoutButton message={message} onClick={onClick} disabled={disabled} />
				</ButtonArea>
			</Container>
		</>
	)
}

const MobileSpacer = styled.div`
	flex-shrink: 0;

	@media (min-width: 768px) {
		display: none;
	}
`

const Container = styled.div<{ $fixed: boolean; $inModal: boolean; $visible: boolean; $keyboardOpen: boolean }>`
	${({ $fixed, $inModal }) => $inModal ? `
		position: sticky;
		bottom: 0;
		left: 0;
		right: 0;

		@media (max-width: 767.98px) {
			position: fixed;
			bottom: 0;
			left: 0;
			right: 0;
			width: 100%;
		}
	` : $fixed ? `
		position: fixed;
		bottom: 0;
		left: 50%;
		right: auto;
		transform: translateX(-50%);
		width: 100%;
	` : `
		position: sticky;
		bottom: 0;
		left: 0;
		right: 0;
	`}
	z-index: 50;
	pointer-events: none;
	${({ $inModal, $visible, $fixed }) => $inModal ? `
		opacity: 1;
	` : `
		opacity: ${$visible ? 1 : 0};
		transform: ${$fixed
		? `translateX(-50%) translateY(${$visible ? '0' : '50px'})`
		: `translateY(${$visible ? '0' : '50px'})`};
		transition: opacity 0.3s ease, transform 0.3s ease;
	`}
`

const Gradient = styled.div<{ $inModal: boolean }>`
	height: ${({ $inModal }) => $inModal ? '16px' : '36px'};
	background: linear-gradient(to top, ${({ $inModal }) => $inModal ? 'var(--card-bg-color)' : 'var(--primary-bg-color)'} 25%, transparent);
	pointer-events: none;

	@media (max-width: 767.98px) {
		height: 36px;
	}
`

const ButtonArea = styled.div<{ $inModal: boolean; $keyboardOpen: boolean }>`
	pointer-events: auto;

	${({ $inModal, $keyboardOpen }) => $keyboardOpen ? `
		padding: 0 12px 8px !important;
		background-color: transparent !important;
	` : `
		padding: 0 ${$inModal ? '0' : '20px'};
		padding-bottom: ${$inModal ? '0' : 'max(env(safe-area-inset-bottom, 0px), 20px)'};

		@media (max-width: 767.98px) {
			padding: 0 20px;
			padding-bottom: max(env(safe-area-inset-bottom, 0px), 20px);
		}
	`}

	background-color: ${({ $inModal }) => $inModal ? 'var(--card-bg-color)' : 'var(--primary-bg-color)'};
`

export default BottomCTA
